import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, StatCard, VehicleImage } from '@/components';
import { useReports } from '@/hooks/useProApi';
import { formatVehicle, formatVin, formatFileSize, utcToLocal } from '@/types';

import clipboardIcon from '@/icons/icons8-clipboard-50.png';
import chartIcon from '@/icons/icons8-chart-50.png';

const reportTypeBadge: Record<string, { label: string; color: string }> = {
  emissions: { label: 'Emissions', color: 'bg-green-500/20 text-green-400' },
  diagnostic: { label: 'Diagnostic', color: 'bg-blue-500/20 text-blue-400' },
  pre_purchase: { label: 'Pre-Purchase', color: 'bg-orange-500/20 text-orange-400' },
  estimate: { label: 'Estimate', color: 'bg-purple-500/20 text-purple-400' },
};

function getReportBadge(type: string | null) {
  return reportTypeBadge[type ?? 'diagnostic'] ?? reportTypeBadge.diagnostic;
}

export function Reports() {
  const { data, isLoading, error } = useReports();
  const reports = data?.reports ?? [];
  const uniqueVins = new Set(reports.map((r) => r.vin).filter(Boolean)).size;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-obdai-gradient-start" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Reports</h1>
        <p className="text-gray-400 mt-1">Cloud-saved diagnostic reports</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Total Reports"
          value={reports.length}
          icon={<img src={clipboardIcon} alt="" className="w-6 h-6" />}
        />
        <StatCard
          label="Vehicles with Reports"
          value={uniqueVins}
          icon={<img src={chartIcon} alt="" className="w-6 h-6" />}
        />
      </div>

      {/* Report List */}
      {error ? (
        <Card>
          <p className="text-red-400 text-center py-8">Failed to load reports.</p>
        </Card>
      ) : reports.length === 0 ? (
        <div className="min-h-[40vh] flex items-center justify-center">
          <Card className="max-w-lg text-center">
            <div className="py-8 px-4">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#6d7cff] to-[#e866dd] flex items-center justify-center shadow-lg shadow-purple-500/20">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-3">No reports yet</h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Generate reports from the mobile app or from a vehicle profile.
              </p>
              <Link
                to="/vehicles"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-purple-500/20 transition"
              >
                View Vehicles
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </Card>
        </div>
      ) : (
        <Card>
          <div className="space-y-2">
            {reports.map((report) => {
              const badge = getReportBadge(report.reportType);
              return (
                <Link
                  key={report.reportId}
                  to={`/reports/${report.reportId}`}
                  className="flex items-center gap-4 p-4 bg-obdai-deep rounded-xl hover:bg-white/5 transition"
                >
                  <VehicleImage vin={report.vin} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {formatVehicle(report.vehicleYear, report.vehicleMake, report.vehicleModel)}
                    </p>
                    <p className="text-gray-500 text-sm font-mono">{formatVin(report.vin)}</p>
                  </div>
                  <div className="hidden md:block flex-1 min-w-0">
                    <p className="text-gray-300 text-sm truncate">{report.reportTitle}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${badge.color}`}>
                    {badge.label}
                  </span>
                  <div className="hidden sm:block text-right min-w-[100px]">
                    <p className="text-gray-400 text-sm">
                      {format(utcToLocal(report.generatedAt), 'MMM d, yyyy')}
                    </p>
                    <p className="text-gray-600 text-xs">{formatFileSize(report.fileSizeBytes)}</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
